import React, { useState } from 'react';

interface KnowledgeStepProps {
    data: {
        knowledge: string[];
        uploadedFiles: File[];
    };
    updateData: (data: Partial<any>) => void;
}

const KnowledgeStep: React.FC<KnowledgeStepProps> = ({ data, updateData }) => {
    const [newSource, setNewSource] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const handleAddSource = () => {
        if (newSource.trim() && !data.knowledge.includes(newSource)) {
            updateData({
                knowledge: [...data.knowledge, newSource]
            });
            setNewSource('');
        }
    };

    const handleRemoveSource = (index: number) => {
        const newKnowledge = [...data.knowledge];
        newKnowledge.splice(index, 1);
        updateData({ knowledge: newKnowledge });
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const newFiles = Array.from(e.target.files);

            // Simulate upload progress
            setIsUploading(true);
            setUploadProgress(0);

            const interval = setInterval(() => {
                setUploadProgress(prev => {
                    if (prev >= 100) {
                        clearInterval(interval);
                        setIsUploading(false);
                        updateData({ uploadedFiles: [...data.uploadedFiles, ...newFiles] });
                        return 0;
                    }
                    return prev + 10;
                });
            }, 300);
        }
    };

    const handleRemoveFile = (index: number) => {
        const newFiles = [...data.uploadedFiles];
        newFiles.splice(index, 1);
        updateData({ uploadedFiles: newFiles });
    };

    return (
        <div className="space-y-6">
            <h2 className="text-xl font-medium text-gray-900">Wissensquellen</h2>
            <p className="text-gray-500">
                Gib Quellen an, aus denen dein Chatbot Informationen beziehen soll. Das können Webseiten, Dokumente oder andere Informationsquellen sein.
            </p>

            <div>
                <label htmlFor="knowledge-source" className="block text-sm font-medium text-gray-700">Webseiten-URLs</label>
                <div className="mt-1 flex rounded-md shadow-sm">
                    <input
                        type="text"
                        name="knowledge-source"
                        id="knowledge-source"
                        value={newSource}
                        onChange={(e) => setNewSource(e.target.value)}
                        className="flex-1 min-w-0 block w-full px-3 py-2 rounded-l-md border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://beispiel.de/faqs"
                    />
                    <button
                        type="button"
                        onClick={handleAddSource}
                        className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 bg-gray-50 rounded-r-md text-gray-500 hover:bg-gray-100"
                    >
                        Hinzufügen
                    </button>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                    Füge URLs zu Webseiten hinzu, die relevante Informationen für deinen Chatbot enthalten.
                </p>

                {/* Liste der hinzugefügten Quellen */}
                {data.knowledge.length > 0 && (
                    <div className="mt-3 space-y-2">
                        <h3 className="text-sm font-medium text-gray-700">Hinzugefügte Quellen:</h3>
                        <ul className="bg-gray-50 rounded-md divide-y divide-gray-200">
                            {data.knowledge.map((source, index) => (
                                <li key={index} className="px-3 py-2 flex items-center justify-between">
                                    <a href={source} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">
                                        {source}
                                    </a>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveSource(index)}
                                        className="ml-2 text-red-500 hover:text-red-700"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700">Dokumente hochladen</label>
                <p className="mt-1 text-sm text-gray-500">
                    Lade Dokumente wie PDFs, Word-Dateien oder TXT-Dateien hoch, um deinem Chatbot mehr Wissen zu geben.
                </p>

                <div className="mt-2 border-2 border-dashed border-gray-300 rounded-md px-6 pt-5 pb-6">
                    <div className="space-y-1 text-center">
                        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div className="flex text-sm text-gray-600">
                            <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                                <span>Dateien hochladen</span>
                                <input
                                    id="file-upload"
                                    name="file-upload"
                                    type="file"
                                    className="sr-only"
                                    multiple
                                    accept=".pdf,.doc,.docx,.txt"
                                    onChange={handleFileUpload}
                                />
                            </label>
                            <p className="pl-1">oder hierher ziehen</p>
                        </div>
                        <p className="text-xs text-gray-500">
                            PDF, DOC, DOCX, TXT bis 10MB
                        </p>
                    </div>
                </div>

                {/* Upload-Fortschritt */}
                {isUploading && (
                    <div className="mt-3">
                        <div className="flex justify-between mb-1">
                            <span className="text-sm font-medium text-blue-700">Uploading...</span>
                            <span className="text-sm font-medium text-blue-700">{uploadProgress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
                        </div>
                    </div>
                )}

                {/* Hochgeladene Dateien */}
                {data.uploadedFiles.length > 0 && (
                    <div className="mt-4">
                        <h3 className="text-sm font-medium text-gray-700">Hochgeladene Dokumente:</h3>
                        <ul className="mt-2 divide-y divide-gray-100">
                            {data.uploadedFiles.map((file, index) => (
                                <li key={index} className="py-2 flex items-center justify-between">
                                    <div className="flex items-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                        </svg>
                                        <span className="ml-2 truncate">{file.name}</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveFile(index)}
                                        className="text-red-500 hover:text-red-700"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                            Je mehr relevante Informationen du bereitstellst, desto besser kann dein Chatbot Fragen beantworten.
                            Achte darauf, dass die Quellen aktuell und zuverlässig sind.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default KnowledgeStep;